import React, { useState, useEffect } from 'react';
import { API, Auth } from 'aws-amplify';
import { NavLink, useLocation } from 'react-router-dom';
import { Flex, Text, Heading, View, Button } from '@aws-amplify/ui-react';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz } from 'ag-grid-community';
import Loadingmini from '../components/loadingmini';
import { useReservationCancelClosePendingSummary } from '../hooks/useReservationCancelClosePendingSummary';
import { withFleets } from '../withFleets';

function fleetsFromSearch(search) {
  const fleetsParam = new URLSearchParams(search).get('fleets');
  if (!fleetsParam) return [];
  return fleetsParam.split(',').map((f) => f.trim()).filter(Boolean);
}

function truthy(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v == null) return false;
  return String(v).toLowerCase() === 'true' || String(v) === '1';
}

function rentalRefundLabel(row) {
  const parts = [];
  parts.push(truthy(row.full_refund) ? 'full refund' : 'no full refund');
  parts.push(truthy(row.cash_refund) ? 'cash' : 'credit');
  if (truthy(row.auto_return_deposit)) parts.push('return deposit');
  return parts.join(' · ');
}

function canKeepOpen(row) {
  if (!row) return false;
  if (truthy(row.stale_approved)) return false;
  const action = String(row.action || '');
  const trigger = String(row.trigger_code || '').toUpperCase();
  if (action === 'close') return true;
  return action === 'cancel' && trigger === 'X1';
}

function formatCreated(val) {
  if (!val) return '';
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleString();
  } catch (e) {
    return String(val);
  }
}

export default function Reservationcancelclosereview() {
  const location = useLocation();
  const selected_fleet = fleetsFromSearch(location.search);
  const fleetCode = selected_fleet[0] || null;
  const [is_loading, updateIsLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [res, updateRes] = useState({ resp: [], total_pending: 0 });
  const [approveRow, updateApproveRow] = useState(null);
  const [keepOpenRow, updateKeepOpenRow] = useState(null);
  const [actionError, updateActionError] = useState('');

  const { refresh: refreshSummary } = useReservationCancelClosePendingSummary({
    enabled: Boolean(fleetCode),
    fleetCode,
    refreshKey: count,
  });

  useEffect(() => {
    let cancelled = false;
    async function fetchPageData() {
      if (!fleetCode) {
        updateRes({ resp: [], total_pending: 0 });
        updateIsLoading(false);
        updateActionError('');
        return;
      }
      updateIsLoading(true);
      updateActionError('');
      try {
        const session = await Auth.currentSession();
        const jwt = session.getAccessToken().getJwtToken();
        const response = await API.get('adminWebTools', '/ago', {
          queryStringParameters: {
            back_end: process.env.REACT_APP_BACKEND,
            jwt,
            api_endpoint: 'api3:staff:cancel_close_review',
            request_type: 'GET',
            mode: 'list',
            fleet_code: fleetCode,
          },
        });
        if (!cancelled) {
          updateRes(response);
        }
      } catch (e) {
        if (!cancelled) {
          updateRes({ resp: [], total_pending: 0, status: 'Error loading queue' });
        }
      }
      if (!cancelled) {
        updateIsLoading(false);
      }
    }
    fetchPageData();
    return () => {
      cancelled = true;
    };
  }, [count, fleetCode]);

  async function postApprove(row) {
    updateIsLoading(true);
    updateActionError('');
    try {
      const session = await Auth.currentSession();
      const jwt = session.getAccessToken().getJwtToken();
      const query = {
        back_end: process.env.REACT_APP_BACKEND,
        jwt,
        api_endpoint: 'api3:staff:cancel_close_review',
        request_type: 'POST',
        method: 'approve',
        proposal_id: row.id,
      };
      const response = await API.get('adminWebTools', '/ago', { queryStringParameters: query });
      updateApproveRow(null);
      if (response?.status && response.status !== 'success') {
        updateActionError(
          response.execution_result || 'Approve ran but execution reported failure. Refresh to confirm.'
        );
      }
      setCount((c) => c + 1);
      refreshSummary();
    } catch (e) {
      updateActionError('Approve failed. Please try again.');
      updateIsLoading(false);
    }
  }

  async function postKeepOpen(row) {
    updateIsLoading(true);
    updateActionError('');
    try {
      const session = await Auth.currentSession();
      const jwt = session.getAccessToken().getJwtToken();
      const query = {
        back_end: process.env.REACT_APP_BACKEND,
        jwt,
        api_endpoint: 'api3:staff:cancel_close_review',
        request_type: 'POST',
        method: 'keep_open',
        proposal_id: row.id,
      };
      const response = await API.get('adminWebTools', '/ago', { queryStringParameters: query });
      updateKeepOpenRow(null);
      if (response?.status && response.status !== 'success') {
        updateActionError(
          response.execution_result || 'Keep open failed. Refresh to confirm.'
        );
      }
      setCount((c) => c + 1);
      refreshSummary();
    } catch (e) {
      updateActionError('Keep open failed. Please try again.');
      updateIsLoading(false);
    }
  }

  const columns = [
    {
      headerName: 'Fleet',
      field: 'fleet_code',
      filter: true,
      width: 100,
    },
    {
      headerName: 'Driver',
      field: 'driver_full_name',
      filter: true,
      width: 160,
      cellRenderer: (params) => {
        const id = params.data?.driver_id;
        if (!id) return params.value;
        return <NavLink to={withFleets(`/user/${id}`, params.data?.fleet_code)}>{params.value || id}</NavLink>;
      },
    },
    {
      headerName: 'Phone',
      field: 'phone',
      filter: true,
      width: 120,
    },
    {
      headerName: 'Res',
      field: 'res_id',
      filter: true,
      width: 110,
      cellRenderer: (params) => {
        const id = params.value;
        if (!id) return null;
        return <NavLink to={withFleets(`/reservation/${id}`, params.data?.fleet_code)}>{id}</NavLink>;
      },
    },
    {
      headerName: 'Next res',
      field: 'related_res_id',
      filter: true,
      width: 110,
      cellRenderer: (params) => {
        const id = params.value;
        if (!id) return null;
        return <NavLink to={withFleets(`/reservation/${id}`, params.data?.fleet_code)}>{id}</NavLink>;
      },
    },
    {
      headerName: 'Trigger',
      field: 'trigger_code',
      filter: true,
      width: 90,
    },
    {
      headerName: 'Action',
      field: 'action',
      filter: true,
      width: 120,
      cellRenderer: (params) => {
        const action = params.value || '';
        const kind = params.data?.close_kind;
        if (action === 'close' && kind) return `${action} (${kind})`;
        return action;
      },
    },
    {
      headerName: 'Reason',
      field: 'reason',
      filter: true,
      flex: 1,
      minWidth: 180,
    },
    {
      headerName: 'Rental refund',
      width: 180,
      valueGetter: (params) => rentalRefundLabel(params.data || {}),
      filter: true,
    },
    {
      headerName: 'Insurance',
      field: 'insurance_summary',
      filter: true,
      width: 160,
    },
    {
      headerName: 'Suspend',
      field: 'suspend_driver',
      width: 90,
      cellRenderer: (params) => (truthy(params.value) ? 'yes' : 'no'),
    },
    {
      headerName: 'Created',
      field: 'created_at',
      filter: true,
      width: 150,
      valueFormatter: (params) => formatCreated(params.value),
    },
    {
      headerName: 'State',
      field: 'state',
      filter: true,
      width: 130,
      cellRenderer: (params) => {
        if (truthy(params.data?.stale_approved)) {
          return <Text fontSize="small" color="#856404">approved (stuck)</Text>;
        }
        return params.value || '';
      },
    },
    {
      headerName: 'Actions',
      width: 210,
      cellRenderer: (params) => {
        const row = params.data;
        if (!row?.id) return null;
        if (!truthy(row.actionable ?? 1) || row.action === 'handoff_warn') {
          return <Text fontSize="small" color="#856404">warn only</Text>;
        }
        return (
          <Flex direction="row" gap="0.25rem" alignItems="center">
            <Button size="small" onClick={() => updateApproveRow(row)}>
              {truthy(row.stale_approved) ? 'Retry' : 'Approve'}
            </Button>
            {canKeepOpen(row) ? (
              <Button size="small" onClick={() => updateKeepOpenRow(row)}>
                Keep open
              </Button>
            ) : null}
          </Flex>
        );
      },
    },
  ];

  const allRows = res.resp || [];
  const actionableRows = allRows.filter((r) => truthy(r.actionable ?? 1) && r.action !== 'handoff_warn');
  const warnRows = allRows.filter((r) => !truthy(r.actionable ?? 1) || r.action === 'handoff_warn');

  return (
    <Flex direction="column" style={{ padding: '1rem', width: '100%' }}>
      <Heading level={4}>Reservation Cancel / Close Review</Heading>
      <Text>
        Approve monitor-proposed reservation cancels and closes for your fleets.
        Keep open leaves the rental running and drops the row until that reservation’s end
        changes and the monitor would fire again.
        Handoff (X2) blocks are warnings only — do not close without opening the next reservation.
        {fleetCode ? ` Filtered to fleet: ${fleetCode}.` : ''}
      </Text>
      {!fleetCode ? (
        <Text style={{ marginTop: '0.75rem' }}>
          <i>Select a fleet using the sidebar fleet selector.</i>
        </Text>
      ) : (
        <>
      <Flex direction="row" gap="0.5rem" alignItems="center" style={{ margin: '0.75rem 0' }}>
        <Button size="small" onClick={() => setCount((c) => c + 1)}>
          Refresh
        </Button>
      </Flex>
      {actionError ? <Text variation="error">{actionError}</Text> : null}
      {is_loading ? (
        <Loadingmini />
      ) : (
        <>
          <Heading level={5} style={{ marginTop: '0.5rem' }}>
            Needs approval ({actionableRows.length})
          </Heading>
          <View className="ag-theme-quartz" style={{ height: 360, width: '100%' }}>
            <AgGridReact
              theme={themeQuartz}
              rowData={actionableRows}
              columnDefs={columns}
              defaultColDef={{ sortable: true, resizable: true }}
            />
          </View>
          <Heading level={5} style={{ marginTop: '1rem' }}>
            Handoff warnings — not actionable ({warnRows.length})
          </Heading>
          <Text fontSize="small" color="#666" style={{ marginBottom: '0.5rem' }}>
            Same-driver adjacent booking blocked (usually insurance). Fix the blocker; monitor will
            hand off automatically when ready. No Approve — closing alone would strand the trip.
          </Text>
          <View className="ag-theme-quartz" style={{ height: 280, width: '100%' }}>
            <AgGridReact
              theme={themeQuartz}
              rowData={warnRows}
              columnDefs={columns.filter((c) => c.headerName !== 'Actions' && c.headerName !== 'Rental refund' && c.headerName !== 'Suspend')}
              defaultColDef={{ sortable: true, resizable: true }}
            />
          </View>
        </>
      )}
      {!is_loading && allRows.length === 0 ? (
        <Text style={{ marginTop: '1rem' }}>
          No pending reservation cancel/close proposals for this fleet.
        </Text>
      ) : null}
        </>
      )}

      {approveRow ? (
        <View
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <View style={{ background: '#fff', padding: '1.5rem', borderRadius: 8, maxWidth: 480, width: '90%' }}>
            <Heading level={5}>
              {truthy(approveRow.stale_approved) ? 'Retry' : 'Approve'} {approveRow.action}
              {approveRow.close_kind ? ` (${approveRow.close_kind})` : ''}?
            </Heading>
            {truthy(approveRow.stale_approved) ? (
              <Text style={{ marginTop: '0.5rem', color: '#856404' }}>
                This proposal was approved earlier but never finished executing. Confirm to run it
                again.
              </Text>
            ) : null}
            <Text style={{ marginTop: '0.5rem' }}>
              {approveRow.driver_full_name} ({approveRow.fleet_code}) — res {approveRow.res_id} —{' '}
              {approveRow.trigger_code}
            </Text>
            <Text style={{ marginTop: '0.75rem' }}>{approveRow.reason}</Text>
            <Text style={{ marginTop: '0.5rem', color: '#555' }}>
              Rental: {rentalRefundLabel(approveRow)}
            </Text>
            {approveRow.insurance_summary ? (
              <Text style={{ marginTop: '0.25rem', color: '#555' }}>
                Insurance: {approveRow.insurance_summary}
              </Text>
            ) : null}
            {truthy(approveRow.suspend_driver) ? (
              <Text style={{ marginTop: '0.25rem', color: '#856404' }}>
                Note: this driver will also be suspended for this fleet.
              </Text>
            ) : null}
            <Flex direction="row" gap="0.5rem" style={{ marginTop: '1rem' }}>
              <Button variation="primary" onClick={() => postApprove(approveRow)}>
                Confirm approve
              </Button>
              <Button onClick={() => updateApproveRow(null)}>Cancel</Button>
            </Flex>
          </View>
        </View>
      ) : null}

      {keepOpenRow ? (
        <View
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <View style={{ background: '#fff', padding: '1.5rem', borderRadius: 8, maxWidth: 480, width: '90%' }}>
            <Heading level={5}>Keep this reservation open?</Heading>
            <Text style={{ marginTop: '0.5rem' }}>
              {keepOpenRow.driver_full_name} ({keepOpenRow.fleet_code}) — res {keepOpenRow.res_id} —{' '}
              {keepOpenRow.trigger_code}
            </Text>
            <Text style={{ marginTop: '0.75rem' }}>{keepOpenRow.reason}</Text>
            <Text style={{ marginTop: '0.75rem', color: '#555' }}>
              This does not close the reservation. It drops off this list. If the rental is still
              past its end, it stays off until you extend. If it is already current, it can come
              back the next time that end lapses and the car is at home.
            </Text>
            <Flex direction="row" gap="0.5rem" style={{ marginTop: '1rem' }}>
              <Button variation="primary" onClick={() => postKeepOpen(keepOpenRow)}>
                Confirm keep open
              </Button>
              <Button onClick={() => updateKeepOpenRow(null)}>Cancel</Button>
            </Flex>
          </View>
        </View>
      ) : null}
    </Flex>
  );
}
